import React, { useState } from 'react';
import { X, Calendar, Download, Mail, Clock, FileText, CheckCircle2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ScheduledReportsModal = ({ isOpen, onClose }) => {
  const [reportConfig, setReportConfig] = useState({
    reportType: 'performance',
    frequency: 'weekly',
    recipients: '',
    format: 'pdf',
    startDate: '',
    time: '09:00',
    includeCharts: true,
    includeDealerships: true,
    includeApplications: true
  });

  const [schedules, setSchedules] = useState([
    {
      id: 1,
      name: 'Wöchentlicher Performance-Bericht',
      type: 'performance',
      frequency: 'weekly',
      nextRun: '22.12.2025 09:00',
      recipients: 'admin@gridbalance.de',
      status: 'active'
    },
    {
      id: 2,
      name: 'Monatlicher Dealership-Bericht',
      type: 'dealership',
      frequency: 'monthly',
      nextRun: '01.01.2026 09:00',
      recipients: 'management@gridbalance.de',
      status: 'active'
    }
  ]);

  const reportTypes = [
    { value: 'performance', label: 'Performance-Übersicht' },
    { value: 'dealership', label: 'Dealership-Analyse' },
    { value: 'application', label: 'Antrags-Statistiken' },
    { value: 'voucher', label: 'Voucher-Metriken' },
    { value: 'comprehensive', label: 'Umfassender Bericht' }
  ];

  const frequencies = [
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'monthly', label: 'Monatlich' },
    { value: 'quarterly', label: 'Vierteljährlich' }
  ];

  const formats = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' }
  ];

  const handleScheduleReport = () => {
    const newSchedule = {
      id: schedules?.length + 1,
      name: `${reportTypes?.find(t => t?.value === reportConfig?.reportType)?.label || ''} - ${frequencies?.find(f => f?.value === reportConfig?.frequency)?.label || ''}`,
      type: reportConfig?.reportType,
      frequency: reportConfig?.frequency,
      nextRun: calculateNextRun(reportConfig?.frequency, reportConfig?.startDate, reportConfig?.time),
      recipients: reportConfig?.recipients,
      status: 'active'
    };

    setSchedules([...schedules, newSchedule]);
    
    setReportConfig({
      reportType: 'performance',
      frequency: 'weekly',
      recipients: '',
      format: 'pdf',
      startDate: '',
      time: '09:00',
      includeCharts: true,
      includeDealerships: true,
      includeApplications: true
    });
  };

  const calculateNextRun = (frequency, startDate, time) => {
    const today = new Date();
    let nextDate = startDate ? new Date(startDate) : today;
    
    switch(frequency) {
      case 'daily':
        nextDate?.setDate(nextDate?.getDate() + 1);
        break;
      case 'weekly':
        nextDate?.setDate(nextDate?.getDate() + 7);
        break;
      case 'monthly':
        nextDate?.setMonth(nextDate?.getMonth() + 1);
        break;
      case 'quarterly':
        nextDate?.setMonth(nextDate?.getMonth() + 3);
        break;
      default:
        break;
    }

    return `${nextDate?.toLocaleDateString('de-DE')} ${time}`;
  };

  const handleDeleteSchedule = (id) => {
    setSchedules(schedules?.filter(s => s?.id !== id));
  };

  const handleGenerateNow = () => {
    console.log('Generating report with config:', reportConfig);
    alert('Bericht wird generiert und per E-Mail versendet...');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground">
                Geplante Berichte
              </h2>
              <p className="text-sm text-muted-foreground">
                Automatische Berichtserstellung konfigurieren
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-160px)]">
          <div className="p-6 space-y-6">
            <div className="bg-muted/50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Neuen Bericht planen
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Berichtstyp"
                  value={reportConfig?.reportType}
                  onChange={(e) => setReportConfig({...reportConfig, reportType: e?.target?.value})}
                  options={reportTypes}
                />

                <Select
                  label="Häufigkeit"
                  value={reportConfig?.frequency}
                  onChange={(e) => setReportConfig({...reportConfig, frequency: e?.target?.value})}
                  options={frequencies}
                />

                <Select
                  label="Format"
                  value={reportConfig?.format}
                  onChange={(e) => setReportConfig({...reportConfig, format: e?.target?.value})}
                  options={formats}
                />

                <Input
                  label="Startdatum"
                  type="date"
                  value={reportConfig?.startDate}
                  onChange={(e) => setReportConfig({...reportConfig, startDate: e?.target?.value})}
                />

                <Input
                  label="Uhrzeit"
                  type="time"
                  value={reportConfig?.time}
                  onChange={(e) => setReportConfig({...reportConfig, time: e?.target?.value})}
                />

                <Input
                  label="E-Mail-Empfänger"
                  type="email"
                  placeholder="admin@gridbalance.de"
                  value={reportConfig?.recipients}
                  onChange={(e) => setReportConfig({...reportConfig, recipients: e?.target?.value})}
                />
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">Berichtsinhalt</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig?.includeCharts}
                      onChange={(e) => setReportConfig({...reportConfig, includeCharts: e?.target?.checked})}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Diagramme einschließen</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig?.includeDealerships}
                      onChange={(e) => setReportConfig({...reportConfig, includeDealerships: e?.target?.checked})}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Dealership-Daten einschließen</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig?.includeApplications}
                      onChange={(e) => setReportConfig({...reportConfig, includeApplications: e?.target?.checked})}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Antrags-Details einschließen</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleScheduleReport}
                  className="flex items-center gap-2"
                  disabled={!reportConfig?.recipients}
                  iconName="Clock"
                >
                  <Clock className="h-4 w-4" />
                  Bericht planen
                </Button>
                <Button
                  variant="outline"
                  onClick={handleGenerateNow}
                  className="flex items-center gap-2"
                  iconName="Download"
                >
                  <Download className="h-4 w-4" />
                  Sofort generieren
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-success" />
                Aktive Berichtspläne ({schedules?.length || 0})
              </h3>

              <div className="space-y-3">
                {schedules?.map((schedule) => (
                  <div
                    key={schedule?.id}
                    className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-semibold text-foreground">{schedule?.name}</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            schedule?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                          }`}>
                            {schedule?.status === 'active' ? 'Aktiv' : 'Pausiert'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{schedule?.frequency === 'weekly' ? 'Wöchentlich' : schedule?.frequency === 'monthly' ? 'Monatlich' : schedule?.frequency}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>Nächster Lauf: {schedule?.nextRun}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            <span className="truncate">{schedule?.recipients}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteSchedule(schedule?.id)}
                        className="text-destructive hover:bg-destructive/10 p-2 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {schedules?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Keine geplanten Berichte vorhanden</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} iconName="X">
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledReportsModal;